import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Hamburger, Search as SearchIcon, Alert } from "./Icons";
import { useUI } from "../data/UIContext";
import { useData } from "../data/DataContext";
import { useAuth } from "../auth/AuthContext";

interface Props {
  onOpenSidebar: () => void;
}

export function Topbar({ onOpenSidebar }: Props) {
  const ui = useUI();
  const data = useData();
  const auth = useAuth();
  const nav = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // `/` focuses search; Esc clears + blurs
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement | null)?.tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const onSearch = (q: string) => {
    ui.setSearchQuery(q);
    if (q.trim()) nav("/search", { replace: true });
  };

  const stats = data.commandCenter?.stats;

  return (
    <header className="topbar">
      <button className="mobile-nav-btn" onClick={onOpenSidebar} aria-label="Open navigation">
        <Hamburger size={20} />
      </button>
      <div className="search-wrap">
        <SearchIcon className="search-icon" size={16} />
        <input
          ref={inputRef}
          className="search"
          type="search"
          placeholder="Search services, integrations, runbooks…"
          autoComplete="off"
          spellCheck={false}
          value={ui.searchQuery}
          onChange={(e) => onSearch(e.target.value)}
        />
        <kbd className="search-kbd">/</kbd>
      </div>
      <div className="topbar-meta">
        {stats ? (
          <>
            <strong>{stats.backend_total + stats.surfaces_total}</strong> services{" · "}
            <strong>{stats.integrations_total}</strong> integrations{" · "}
            <strong>{stats.clients_total}</strong> clients{" · "}
            <strong>{stats.alerts_total}</strong> alerts
          </>
        ) : "loading…"}
      </div>
      <button className="declare-btn" type="button"
              disabled={!auth.canDeclare}
              title={auth.canDeclare
                ? "Declare an incident (i)"
                : "You need an on-call / ops role to declare incidents"}
              onClick={() => ui.setDeclareOpen(true)}>
        <Alert size={14} />
        <span>Declare incident</span>
      </button>
    </header>
  );
}
